import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import type { SwiperWrapperProps } from '@/core/features/swiper/swiper-wrapper/swiper-types';
// eslint-disable-next-line import-rules/disallow-paths
import type { Swiper as SwiperClass } from 'swiper';

import React from 'react';

// eslint-disable-next-line import-rules/disallow-paths
import { Swiper, SwiperSlide } from 'swiper/react';

import { swiperModule } from '@/core/features/swiper/swiper-wrapper/swiper-module-config';
import { bemModule, classnames } from '@/core/utils/bem-classname';
import { isReactChildElement } from '@/core/utils/react';

import styles from './swiper-wrapper.module.scss';

const bem = bemModule(styles);

export default function SwiperWrapper(props: SwiperWrapperProps) {
    const [activeSwiperSlideIndex, setActiveSlideIndex] = React.useState(0);

    const moduleProps = { ...swiperModule[props.swiperModuleType], ...props.swiperModuleOverrides };
    const {
        allowTouchMove = moduleProps.allowTouchMove,
        breakpoints,
        children,
        disableAutoSwiperSlideIndex = false,
        disableLazyLoading = false,
        disableSwiperZIndex = false,
        enabled = true,
        hasAlignItemsEnd = false,
        initialSlide = moduleProps.initialSlide,
        loop = moduleProps.loop,
        navigation = moduleProps.navigation,
        onNavigationNext,
        onNavigationPrev,
        onResize,
        onSlideChange,
        onSwiper,
        slidesPerView = moduleProps.slidesPerView,
        swiperModuleType,
        swiperNavigation = 'default',
        swiperSlideProps,
    } = props;
    const _onSlideChange = (swiper: SwiperClass) => {
        onSlideChange?.(swiper);
        setActiveSlideIndex(swiper.realIndex);
    };
    const slidesPerViewCount = slidesPerView === 'auto' ? 3 : (slidesPerView ?? 1);
    const adjacentSlideCount = Math.max(1, Math.floor(slidesPerViewCount / 2));
    const maxRenderIndex = activeSwiperSlideIndex + slidesPerViewCount + adjacentSlideCount;
    const minRenderIndex = activeSwiperSlideIndex - adjacentSlideCount;
    const slideCount = Array.isArray(children) ? children.length : 0;
    const loopedIndexStart = loop && slideCount >= slidesPerViewCount ? slideCount - slidesPerViewCount : null;
    return (
        <Swiper
            {...moduleProps}
            allowTouchMove={allowTouchMove}
            breakpoints={breakpoints}
            className={classnames(
                swiperNavigation,
                bem(styles.swiperWrapper, {
                    disableSwiperZIndex,
                    hasAlignItemsEnd,
                    [swiperModuleType]: swiperModuleType === 'contentBoxSwiperDesktop',
                }),
                styles.swiperOverrides,
            )}
            enabled={enabled}
            initialSlide={initialSlide}
            loop={loop}
            navigation={navigation}
            onNavigationNext={onNavigationNext}
            onNavigationPrev={onNavigationPrev}
            onResize={onResize}
            onSlideChange={_onSlideChange}
            onSwiper={onSwiper}
            slidesPerView={slidesPerView}
        >
            {React.Children.map(children, (child, index) => {
                if (!isReactChildElement(child)) {
                    return null;
                }
                const isLoopedSlideIndex = loopedIndexStart && index >= loopedIndexStart;
                const shouldHideSlide = index > maxRenderIndex || index < minRenderIndex;
                const slideStyle = { zIndex: !disableAutoSwiperSlideIndex ? slideCount - index : undefined };
                if (!disableLazyLoading && shouldHideSlide && !isLoopedSlideIndex) {
                    return (
                        <SwiperSlide
                            {...swiperSlideProps}
                            style={{ ...slideStyle, ...swiperSlideProps?.style }}
                        />
                    );
                }
                return (
                    <SwiperSlide
                        {...swiperSlideProps}
                        style={{ ...slideStyle, ...swiperSlideProps?.style }}
                    >
                        {child}
                    </SwiperSlide>
                );
            })}
        </Swiper>
    );
}
